import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";
import {
  LayoutDashboard, FileText, Briefcase, Linkedin, Users, MessageSquare,
  Settings, LogOut, Plus, Trash2, Edit, Check, X, Upload, ExternalLink,
  BookOpen, Clock, Eye, EyeOff, UserPlus, Bot, Layers, Video, Save, Star
} from "lucide-react";
import { API_BASE_URL, BACKEND_URL } from "../lib/site";

const API = API_BASE_URL;
const BACKEND = BACKEND_URL;
const TOKEN_KEY = "np_admin_token";
const getToken = () => localStorage.getItem(TOKEN_KEY);
const authH = () => ({ Authorization: `Bearer ${getToken()}` });
const backendConfigured = Boolean(BACKEND);

// ─── Login ───
function LoginPage({ onLogin }) {
  const initialResetToken = new URLSearchParams(window.location.search).get("reset") || "";
  const [form, setForm] = useState({ email: "", password: "" });
  const [mode, setMode] = useState(initialResetToken ? "reset" : "login");
  const [resetToken, setResetToken] = useState(initialResetToken);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [devResetUrl, setDevResetUrl] = useState("");
  const [resetForm, setResetForm] = useState({ password: "", confirm: "" });
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.email);
    } catch (err) {
      if (err?.response?.data?.detail) setError(err.response.data.detail);
      else if (!backendConfigured || err?.request) setError("Admin backend is not configured or reachable from this preview yet.");
      else setError("Invalid credentials. Please try again.");
    } finally { setLoading(false); }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");
    setDevResetUrl("");
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      if (res.data?.delivery === "email") {
        setForgotMessage("Reset link sent. Check your inbox.");
      } else if (res.data?.delivery === "dev-link" && res.data?.reset_url) {
        setForgotMessage("Local reset link generated. Open it below.");
        setDevResetUrl(res.data.reset_url);
      } else {
        setForgotMessage("If that email matches the admin account, a reset link has been sent.");
      }
    } catch (err) {
      if (err?.response?.data?.detail) setForgotError(err.response.data.detail);
      else if (!backendConfigured || err?.request) setForgotError("Admin backend is not configured or reachable from this preview yet.");
      else setForgotError("Unable to start password reset right now.");
    } finally {
      setForgotLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    if (!resetForm.password || resetForm.password.length < 8) {
      setResetError("Password must be at least 8 characters long.");
      return;
    }
    if (resetForm.password !== resetForm.confirm) {
      setResetError("Passwords do not match.");
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: resetForm.password });
      window.history.replaceState({}, "", "/admin");
      setMode("login");
      setResetToken("");
      setResetForm({ password: "", confirm: "" });
      setInfo("Password updated. Sign in with your new password.");
    } catch (err) {
      if (err?.response?.data?.detail) setResetError(err.response.data.detail);
      else if (!backendConfigured || err?.request) setResetError("Admin backend is not configured or reachable from this preview yet.");
      else setResetError("Unable to reset password.");
    } finally {
      setResetLoading(false);
    }
  };

  const backToLogin = () => {
    setMode("login");
    setError("");
    setForgotError("");
    setForgotMessage("");
    setDevResetUrl("");
    setResetError("");
    if (window.location.search.includes("reset=")) {
      window.history.replaceState({}, "", "/admin");
      setResetToken("");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="w-full max-w-sm bg-white border border-[#E5E7EB] p-8">
        <div className="mb-8">
          <p className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">Admin Panel</p>
          <p className="text-sm text-[#6B7280] mt-1">
            {mode === "login" && "Niharika Patel — Content Studio"}
            {mode === "forgot" && "Request a password reset link"}
            {mode === "reset" && "Set a new admin password"}
          </p>
        </div>
        {!backendConfigured && (
          <p className="text-amber-700 text-sm bg-amber-50 border border-amber-200 px-3 py-2 mb-4">
            This local preview does not have a backend URL configured yet, so admin login and uploads will not work until the API is connected.
          </p>
        )}

        {mode === "login" && (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {info && <p className="text-[#002FA7] text-sm bg-blue-50 border border-blue-100 px-3 py-2">{info}</p>}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]" data-testid="login-email" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Password</label>
              <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]" data-testid="login-password" />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white py-3 font-semibold hover:bg-[#001D66] transition-colors disabled:opacity-50" data-testid="login-submit">
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("forgot");
                setError("");
                setInfo("");
                setForgotEmail(form.email);
              }}
              className="text-sm text-[#002FA7] hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={requestReset} className="space-y-4" data-testid="forgot-password-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Admin Email</label>
              <input
                type="email"
                required
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            {forgotError && <p className="text-red-500 text-sm">{forgotError}</p>}
            {forgotMessage && <p className="text-[#002FA7] text-sm bg-blue-50 border border-blue-100 px-3 py-2">{forgotMessage}</p>}
            {devResetUrl && (
              <a href={devResetUrl} className="text-sm text-[#002FA7] hover:underline break-all" data-testid="dev-reset-link">
                {devResetUrl}
              </a>
            )}
            <button type="submit" disabled={forgotLoading} className="w-full bg-[#002FA7] text-white py-3 font-semibold hover:bg-[#001D66] transition-colors disabled:opacity-50">
              {forgotLoading ? "Sending..." : "Send Reset Link"}
            </button>
            <button type="button" onClick={backToLogin} className="text-sm text-[#4B5563] hover:text-[#0A0A0A]">
              Back to sign in
            </button>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={submitNewPassword} className="space-y-4" data-testid="reset-password-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">New Password</label>
              <input
                type="password"
                required
                value={resetForm.password}
                onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-1.5">Confirm Password</label>
              <input
                type="password"
                required
                value={resetForm.confirm}
                onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            {resetError && <p className="text-red-500 text-sm">{resetError}</p>}
            <button type="submit" disabled={resetLoading} className="w-full bg-[#002FA7] text-white py-3 font-semibold hover:bg-[#001D66] transition-colors disabled:opacity-50">
              {resetLoading ? "Updating..." : "Update Password"}
            </button>
            <button type="button" onClick={backToLogin} className="text-sm text-[#4B5563] hover:text-[#0A0A0A]">
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Overview Tab ───
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authH() }).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/funnel`, { headers: authH() }).then((r) => setFunnel(r.data)).catch(() => {});
  }, []);

  const cards = stats ? [
    { label: "Leads", value: stats.leads, icon: UserPlus, color: "text-[#002FA7]" },
    { label: "Subscribers", value: stats.subscribers, icon: Users, color: "text-blue-500" },
    { label: "Articles", value: `${stats.published_articles}/${stats.articles}`, icon: FileText, color: "text-[#0A0A0A]" },
    { label: "Pending Comments", value: stats.comments_pending, icon: MessageSquare, color: "text-orange-500" },
    { label: "Pending Reviews", value: stats.reviews_pending, icon: Star, color: "text-amber-500" },
    { label: "Case Studies", value: stats.case_studies, icon: Briefcase, color: "text-purple-600" },
  ] : [];

  return (
    <div data-testid="overview-tab">
      <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-6">Overview</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="border border-[#E5E7EB] bg-white p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-[#6B7280] uppercase tracking-wide">{c.label}</p>
              <c.icon size={16} className={c.color} />
            </div>
            <p className={`font-['Outfit'] text-3xl font-bold ${c.color}`}>{c.value ?? "—"}</p>
          </div>
        ))}
      </div>
      {funnel.length > 0 && (
        <div className="border border-[#E5E7EB] bg-white p-6">
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#0A0A0A] mb-4">Subscriber Sources</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={funnel} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="source" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#002FA7" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Site Content Tab ───
const SC_DEFAULTS = {
  hero_headline_line1: "Building brands", hero_headline_line2: "consumers trust.",
  hero_subheading: "~10 years across Amazon, Nestle, Zepto, Dabur and Emami. Now advising B2C startups on integrated growth — brand, performance, and CRM as one system.",
  hero_bottom_label: "~10 years building", hero_monogram_subtitle: "Amazon · Nestle · Zepto · Dabur · Emami",
  hero_stats: [{ value: "~20%", label: "MoM growth at Zepto", accent: true }, { value: "₹40Cr+", label: "Monthly budget managed", accent: false }, { value: "22%", label: "CAC reduction at Amazon", accent: false }, { value: "43%", label: "ROI improvement, FMCG", accent: true }],
  marquee_items: [{ value: "~10 Years", label: "Experience" }, { value: "₹40Cr+", label: "Monthly budgets managed" }, { value: "43%", label: "Avg. ROI improvement" }, { value: "2x", label: "Category growth delivered" }, { value: "Amazon • Nestle • Zepto • Dabur • Emami", label: "Career highlights" }],
  hero_cta_primary: "Work with me", hero_cta_secondary: "Read my writing",
  articles_section_label: "Knowledge", articles_section_heading: "Latest Thinking", articles_link_text: "All writing",
  case_studies_section_label: "Evidence", case_studies_section_heading: "Case Studies",
  about_bio_intro: "~10 years across Amazon, Nestle, Zepto, Dabur and Emami.",
  about_bio_story: "",
  about_achievements: [{ metric: "~20%", label: "MoM growth at Zepto" }, { metric: "₹40Cr+", label: "Monthly budget at Amazon" }, { metric: "2x", label: "Category growth" }, { metric: "43%", label: "ROI improvement" }],
  experience: [],
  skills: [],
  education: [],
  award_title: "Customer Obsession Award", award_org: "Amazon India",
};

function ContentTab() {
  const [sc, setSc] = useState(SC_DEFAULTS);
  const [section, setSection] = useState("homepage");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/site-content`, { headers: authH() }).then((r) => setSc({ ...SC_DEFAULTS, ...r.data })).catch(() => {});
  }, []);

  const saveSection = async (partial) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-content`, { ...sc, ...partial }, { headers: authH() });
      setSc((prev) => ({ ...prev, ...partial }));
      toast.success("Content saved — refresh the website to see changes");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  // helpers for array items
  const updateHeroStat = (i, field, val) => {
    const next = sc.hero_stats.map((s, j) => j === i ? { ...s, [field]: val } : s);
    setSc({ ...sc, hero_stats: next });
  };
  const updateMarquee = (i, field, val) => {
    const next = sc.marquee_items.map((m, j) => j === i ? { ...m, [field]: val } : m);
    setSc({ ...sc, marquee_items: next });
  };
  const updateAchievement = (i, field, val) => {
    const next = sc.about_achievements.map((a, j) => j === i ? { ...a, [field]: val } : a);
    setSc({ ...sc, about_achievements: next });
  };
  const updateExp = (i, field, val) => {
    const next = (sc.experience || []).map((e, j) => j === i ? { ...e, [field]: val } : e);
    setSc({ ...sc, experience: next });
  };
  const addExp = () => setSc({ ...sc, experience: [...(sc.experience || []), { role: "", company: "", period: "", highlight: false }] });
  const delExp = (i) => setSc({ ...sc, experience: (sc.experience || []).filter((_, j) => j !== i) });
  const updateEdu = (i, field, val) => {
    const next = (sc.education || []).map((e, j) => j === i ? { ...e, [field]: val } : e);
    setSc({ ...sc, education: next });
  };
  const addEdu = () => setSc({ ...sc, education: [...(sc.education || []), { degree: "", school: "" }] });
  const delEdu = (i) => setSc({ ...sc, education: (sc.education || []).filter((_, j) => j !== i) });

  const SECTIONS = [
    { id: "homepage", label: "Homepage", path: "/" },
    { id: "about", label: "About Page", path: "/about" },
  ];

  const previewUrl = `${BACKEND}${SECTIONS.find((s) => s.id === section)?.path || "/"}`;

  return (
    <div data-testid="content-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280]">Site Content</p>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 border border-[#E5E7EB] p-0.5">
            {SECTIONS.map((s) => (
              <button key={s.id} onClick={() => setSection(s.id)} className={`px-4 py-1.5 text-xs font-semibold transition-colors ${section === s.id ? "bg-[#002FA7] text-white" : "text-[#6B7280] hover:bg-[#F3F4F6]"}`}>{s.label}</button>
            ))}
          </div>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#002FA7] px-3 py-1.5 hover:bg-[#001D66] transition-colors"
            data-testid="preview-page-btn"
          >
            <ExternalLink size={12} /> Preview
          </a>
        </div>
      </div>

      {section === "homepage" && (
        <div className="space-y-6">
          {/* Hero Text */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-4">Hero Headline</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div><label className="admin-label">Line 1 (light)</label><input className="admin-input" value={sc.hero_headline_line1} onChange={(e) => setSc({ ...sc, hero_headline_line1: e.target.value })} /></div>
              <div><label className="admin-label">Line 2 (bold)</label><input className="admin-input" value={sc.hero_headline_line2} onChange={(e) => setSc({ ...sc, hero_headline_line2: e.target.value })} /></div>
            </div>
            <div className="mb-3"><label className="admin-label">Hero Subheading</label><textarea rows={2} className="admin-input" value={sc.hero_subheading} onChange={(e) => setSc({ ...sc, hero_subheading: e.target.value })} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="admin-label">Bottom Strip Label (e.g. "~10 years building")</label><input className="admin-input" value={sc.hero_bottom_label} onChange={(e) => setSc({ ...sc, hero_bottom_label: e.target.value })} /></div>
              <div><label className="admin-label">Monogram Card Subtitle (e.g. "Amazon · Nestle · Zepto · Dabur · Emami")</label><input className="admin-input" value={sc.hero_monogram_subtitle} onChange={(e) => setSc({ ...sc, hero_monogram_subtitle: e.target.value })} /></div>
            </div>
          </div>

          {/* Hero Stats Cards */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-4">Hero Stats (4 cards)</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {(sc.hero_stats || []).map((s, i) => (
                <div key={i} className={`border p-3 ${s.accent ? "border-[#002FA7]/30 bg-blue-50/30" : "border-[#E5E7EB]"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase text-[#6B7280]">Card {i + 1}</span>
                    <label className="flex items-center gap-1 text-xs text-[#6B7280]">
                      <input type="checkbox" checked={s.accent} onChange={(e) => updateHeroStat(i, "accent", e.target.checked)} className="accent-[#002FA7]" /> Blue accent
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="admin-label">Value</label><input className="admin-input text-sm" value={s.value} onChange={(e) => updateHeroStat(i, "value", e.target.value)} data-testid={`hero-stat-value-${i}`} /></div>
                    <div><label className="admin-label">Label</label><input className="admin-input text-sm" value={s.label} onChange={(e) => updateHeroStat(i, "label", e.target.value)} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Marquee Items */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-1">Marquee / Ticker Items</p>
            <p className="text-xs text-[#6B7280] mb-4">These scroll across the homepage band below the hero.</p>
            <div className="space-y-2">
              {(sc.marquee_items || []).map((m, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 items-center">
                  <div><label className="admin-label">Value (large text)</label><input className="admin-input text-sm" value={m.value} onChange={(e) => updateMarquee(i, "value", e.target.value)} /></div>
                  <div className="flex gap-2">
                    <div className="flex-1"><label className="admin-label">Label (small text)</label><input className="admin-input text-sm" value={m.label} onChange={(e) => updateMarquee(i, "label", e.target.value)} /></div>
                    <button onClick={() => setSc({ ...sc, marquee_items: sc.marquee_items.filter((_, j) => j !== i) })} className="mt-4 p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => setSc({ ...sc, marquee_items: [...(sc.marquee_items || []), { value: "", label: "" }] })} className="text-xs text-[#002FA7] flex items-center gap-1 mt-2"><Plus size={12} /> Add item</button>
            </div>
          </div>

          {/* Hero CTA Buttons */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-1">Hero CTA Buttons</p>
            <p className="text-xs text-[#6B7280] mb-4">The two action buttons shown below the hero headline.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="admin-label">Primary button text (e.g. "Work with me")</label><input className="admin-input" value={sc.hero_cta_primary || ""} onChange={(e) => setSc({ ...sc, hero_cta_primary: e.target.value })} data-testid="hero-cta-primary-input" /></div>
              <div><label className="admin-label">Secondary button text (e.g. "Read my writing")</label><input className="admin-input" value={sc.hero_cta_secondary || ""} onChange={(e) => setSc({ ...sc, hero_cta_secondary: e.target.value })} data-testid="hero-cta-secondary-input" /></div>
            </div>
          </div>

          {/* Section Labels */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-1">Homepage Section Labels</p>
            <p className="text-xs text-[#6B7280] mb-4">Edit the small label (blue, uppercase) and heading for each homepage section.</p>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">Articles Section</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><label className="admin-label">Small label (e.g. "Knowledge")</label><input className="admin-input" value={sc.articles_section_label || ""} onChange={(e) => setSc({ ...sc, articles_section_label: e.target.value })} data-testid="articles-section-label-input" /></div>
                  <div><label className="admin-label">Section heading (e.g. "Latest Thinking")</label><input className="admin-input" value={sc.articles_section_heading || ""} onChange={(e) => setSc({ ...sc, articles_section_heading: e.target.value })} data-testid="articles-section-heading-input" /></div>
                  <div><label className="admin-label">"See all" link text (e.g. "All writing")</label><input className="admin-input" value={sc.articles_link_text || ""} onChange={(e) => setSc({ ...sc, articles_link_text: e.target.value })} data-testid="articles-link-text-input" /></div>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">Case Studies Section</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><label className="admin-label">Small label (e.g. "Evidence")</label><input className="admin-input" value={sc.case_studies_section_label || ""} onChange={(e) => setSc({ ...sc, case_studies_section_label: e.target.value })} data-testid="case-studies-section-label-input" /></div>
                  <div><label className="admin-label">Section heading (e.g. "Case Studies")</label><input className="admin-input" value={sc.case_studies_section_heading || ""} onChange={(e) => setSc({ ...sc, case_studies_section_heading: e.target.value })} data-testid="case-studies-section-heading-input" /></div>
                </div>
              </div>
            </div>
          </div>

          <button disabled={saving} onClick={() => saveSection({})} className="bg-[#002FA7] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#001D66] flex items-center gap-2 disabled:opacity-50" data-testid="save-homepage-content-btn">
            <Save size={13} /> {saving ? "Saving..." : "Save Homepage Content"}
          </button>
        </div>
      )}

      {section === "about" && (
        <div className="space-y-6">
          {/* Bio */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-4">About Page Bio</p>
            <div className="mb-3"><label className="admin-label">Opening paragraph (shown large, next to photo)</label><textarea rows={3} className="admin-input" value={sc.about_bio_intro} onChange={(e) => setSc({ ...sc, about_bio_intro: e.target.value })} /></div>
            <div><label className="admin-label">Full story (The Story section) — separate paragraphs with a blank line between them</label><textarea rows={10} className="admin-input font-mono text-xs leading-relaxed" value={sc.about_bio_story} onChange={(e) => setSc({ ...sc, about_bio_story: e.target.value })} /></div>
          </div>

          {/* Achievements */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-4">Key Numbers (4 achievement cards)</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {(sc.about_achievements || []).map((a, i) => (
                <div key={i} className="border border-[#E5E7EB] p-3">
                  <p className="text-[10px] font-bold uppercase text-[#6B7280] mb-2">Card {i + 1}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="admin-label">Metric</label><input className="admin-input text-sm" value={a.metric} onChange={(e) => updateAchievement(i, "metric", e.target.value)} /></div>
                    <div><label className="admin-label">Label</label><input className="admin-input text-sm" value={a.label} onChange={(e) => updateAchievement(i, "label", e.target.value)} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Experience Timeline */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-sm text-[#0A0A0A]">Experience Timeline</p>
              <button onClick={addExp} className="text-xs text-[#002FA7] flex items-center gap-1"><Plus size={12} /> Add role</button>
            </div>
            <div className="space-y-3">
              {(sc.experience || []).map((e, i) => (
                <div key={i} className={`border p-3 ${e.highlight ? "border-[#002FA7]/30 bg-blue-50/30" : "border-[#E5E7EB]"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-1 text-xs text-[#6B7280]">
                      <input type="checkbox" checked={e.highlight} onChange={(ev) => updateExp(i, "highlight", ev.target.checked)} className="accent-[#002FA7]" /> Highlight (current role)
                    </label>
                    <button onClick={() => delExp(i)} className="p-1 text-red-500 hover:bg-red-50"><Trash2 size={12} /></button>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <div><label className="admin-label">Role / Title</label><input className="admin-input text-sm" value={e.role} onChange={(ev) => updateExp(i, "role", ev.target.value)} /></div>
                    <div><label className="admin-label">Company</label><input className="admin-input text-sm" value={e.company} onChange={(ev) => updateExp(i, "company", ev.target.value)} /></div>
                    <div><label className="admin-label">Period (e.g. Jan 2026 – Present)</label><input className="admin-input text-sm" value={e.period} onChange={(ev) => updateExp(i, "period", ev.target.value)} /></div>
                  </div>
                </div>
              ))}
              {(!sc.experience || sc.experience.length === 0) && <p className="text-xs text-[#9CA3AF] py-4 text-center">No experience entries yet. Click "Add role" above.</p>}
            </div>
          </div>

          {/* Skills */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-1">Skills</p>
            <p className="text-xs text-[#6B7280] mb-3">Comma-separated list of skills shown on the About page.</p>
            <textarea rows={3} className="admin-input" value={(sc.skills || []).join(", ")} onChange={(e) => setSc({ ...sc, skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
          </div>

          {/* Education */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-sm text-[#0A0A0A]">Education</p>
              <button onClick={addEdu} className="text-xs text-[#002FA7] flex items-center gap-1"><Plus size={12} /> Add</button>
            </div>
            <div className="space-y-2">
              {(sc.education || []).map((e, i) => (
                <div key={i} className="flex gap-2 items-end">
                  <div className="flex-1"><label className="admin-label">Degree / Qualification</label><input className="admin-input text-sm" value={e.degree} onChange={(ev) => updateEdu(i, "degree", ev.target.value)} /></div>
                  <div className="flex-1"><label className="admin-label">School / Institution</label><input className="admin-input text-sm" value={e.school} onChange={(ev) => updateEdu(i, "school", ev.target.value)} /></div>
                  <button onClick={() => delEdu(i)} className="mb-1 p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Award */}
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-semibold text-sm text-[#0A0A0A] mb-3">Award</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="admin-label">Award Title</label><input className="admin-input" value={sc.award_title || ""} onChange={(e) => setSc({ ...sc, award_title: e.target.value })} /></div>
              <div><label className="admin-label">Organisation</label><input className="admin-input" value={sc.award_org || ""} onChange={(e) => setSc({ ...sc, award_org: e.target.value })} /></div>
            </div>
          </div>

          <button disabled={saving} onClick={() => saveSection({})} className="bg-[#002FA7] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#001D66] flex items-center gap-2 disabled:opacity-50" data-testid="save-about-content-btn">
            <Save size={13} /> {saving ? "Saving..." : "Save About Page Content"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Articles Tab ───
function ArticlesTab() {
  const [articles, setArticles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", published: false, read_time: 5 });
  const [imgUploading, setImgUploading] = useState(false);
  const [inlineImgUploading, setInlineImgUploading] = useState(false);
  const [importingDocument, setImportingDocument] = useState(false);
  const imgRef = useRef();
  const inlineImgRef = useRef();
  const importDocRef = useRef();

  const load = () => axios.get(`${API}/articles`, { headers: authH() }).then((r) => setArticles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", published: false, read_time: 5 }); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a, tags: (a.tags || []).join(", ") }); setShowForm(true); };

  const uploadCoverImage = async () => {
    const file = imgRef.current?.files?.[0];
    if (!file) return;
    setImgUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/upload`, fd, { headers: authH() });
      setForm((prev) => ({ ...prev, cover_image: r.data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Image upload failed"); }
    setImgUploading(false);
  };

  const uploadInlineImage = async () => {
    const file = inlineImgRef.current?.files?.[0];
    if (!file) return;
    setInlineImgUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/upload`, fd, { headers: authH() });
      const alt = file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim() || "Article image";
      setForm((prev) => {
        const trimmed = (prev.content || "").replace(/\s+$/, "");
        const spacer = trimmed ? "\n\n" : "";
        return {
          ...prev,
          content: `${trimmed}${spacer}![${alt}](${r.data.url})\n`,
        };
      });
      toast.success("Image uploaded and inserted into the article");
    } catch {
      toast.error("Inline image upload failed");
    }
    setInlineImgUploading(false);
  };

  const importDocument = async () => {
    const file = importDocRef.current?.files?.[0];
    if (!file) return;
    setImportingDocument(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/articles/import-document`, fd, { headers: authH() });
      const imported = r.data;
      await load();
      setEditing(imported);
      setForm({ ...imported, tags: (imported.tags || []).join(", ") });
      setShowForm(true);
      toast.success(imported.import_notice || "Document imported as a draft article");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Document import failed");
    } finally {
      if (importDocRef.current) importDocRef.current.value = "";
      setImportingDocument(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean), read_time: Number(form.read_time) };
    try {
      if (editing) { await axios.put(`${API}/articles/${editing.id}`, payload, { headers: authH() }); toast.success("Article updated"); }
      else { await axios.post(`${API}/articles`, payload, { headers: authH() }); toast.success("Article created"); }
      setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error saving article"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await axios.delete(`${API}/articles/${id}`, { headers: authH() });
    toast.success("Article deleted");
    load();
  };

  const togglePublish = async (a) => {
    await axios.put(`${API}/articles/${a.id}`, { published: !a.published }, { headers: authH() });
    toast.success(a.published ? "Article set to Draft" : "Article published — refresh the website to see it live");
    load();
  };

  return (
    <div data-testid="articles-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280]">Articles ({articles.length})</p>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer border border-[#E5E7EB] bg-white text-[#0A0A0A] text-xs px-4 py-2 font-semibold hover:bg-[#F3F4F6] transition-colors flex items-center gap-1">
            <Upload size={12} /> {importingDocument ? "Importing..." : "Import Word/PDF"}
            <input
              ref={importDocRef}
              type="file"
              accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={importDocument}
            />
          </label>
          <button onClick={openNew} className="bg-[#002FA7] text-white text-xs px-4 py-2 font-semibold hover:bg-[#001D66] transition-colors flex items-center gap-1" data-testid="new-article-btn">
            <Plus size={12} /> New Article
          </button>
        </div>
      </div>
      <p className="text-xs text-[#6B7280] mb-6">
        For Google Docs, download as Microsoft Word (.docx) for the cleanest import. PDF import works, but tables and image placement may need more cleanup.
      </p>

      {showForm && (
        <div className="border border-[#E5E7EB] bg-white p-6 mb-6" data-testid="article-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['Outfit'] font-semibold text-[#0A0A0A]">{editing ? "Edit Article" : "New Article"}</h3>
            <button onClick={() => setShowForm(false)}><X size={16} /></button>
          </div>
          <form onSubmit={save} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="admin-label">Title *</label>
                <input required className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: !editing ? e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") : form.slug })} data-testid="article-title" /></div>
              <div><label className="admin-label">Slug *</label>
                <input required className="admin-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="article-slug" /></div>
            </div>
            <div><label className="admin-label">Excerpt</label>
              <textarea className="admin-input" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
            <div>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <label className="admin-label mb-0">Content (Markdown)</label>
                <label className="cursor-pointer bg-[#F3F4F6] border border-[#E5E7EB] px-3 py-2 text-xs font-medium hover:bg-[#E5E7EB] flex items-center gap-1 whitespace-nowrap">
                  <Upload size={12} /> {inlineImgUploading ? "Uploading..." : "Add inline image"}
                  <input ref={inlineImgRef} type="file" accept="image/*" className="hidden" onChange={uploadInlineImage} />
                </label>
              </div>
              <p className="text-[11px] text-[#6B7280] mb-2">
                Best for Google Docs: either paste clean text here, or use “Import Word/PDF” above to create a draft automatically. If an imported document looks technical in this box, that is normal — the website will render the article properly.
              </p>
              <textarea className="admin-input font-mono text-xs" rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="article-content" />
            </div>
            <div>
              <label className="admin-label">Cover Image</label>
              <div className="flex gap-2 items-center">
                <input className="admin-input flex-1" placeholder="Paste URL or upload below" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
                <label className="cursor-pointer bg-[#F3F4F6] border border-[#E5E7EB] px-3 py-2 text-xs font-medium hover:bg-[#E5E7EB] flex items-center gap-1 whitespace-nowrap" data-testid="upload-cover-btn">
                  <Upload size={12} /> {imgUploading ? "Uploading..." : "Upload"}
                  <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadCoverImage} />
                </label>
              </div>
              {form.cover_image && (
                <img src={form.cover_image.startsWith("/api") ? `${BACKEND}${form.cover_image}` : form.cover_image} alt="" className="mt-2 h-20 object-cover border border-[#E5E7EB]" onError={(e) => { e.target.style.display = "none"; }} />
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="admin-label">Tags (comma-separated)</label>
                <input className="admin-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
              <div><label className="admin-label">Read Time (min)</label>
                <input type="number" className="admin-input" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#002FA7]" />
              <label htmlFor="published" className="text-sm text-[#0A0A0A]">Published (visible on website)</label>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#001D66]" data-testid="save-article-btn">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-[#E5E7EB] px-5 py-2 text-sm hover:bg-[#F3F4F6]">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-2">
        {articles.map((a) => (
          <div key={a.id} className="border border-[#E5E7EB] bg-white p-4 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-[#0A0A0A] truncate">{a.title}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-[10px] px-2 py-0.5 font-bold ${a.published ? "bg-green-50 text-green-700" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                  {a.published ? "Published" : "Draft"}
                </span>
                <span className="text-xs text-[#9CA3AF] flex items-center gap-1"><Clock size={10} /> {a.read_time}m</span>
                {a.published && (
                  <a href={`/writing/${a.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline flex items-center gap-0.5">
                    <ExternalLink size={10} /> View
                  </a>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => togglePublish(a)} title={a.published ? "Set to Draft" : "Publish"} className={`p-2 border transition-colors ${a.published ? "border-orange-100 text-orange-500 hover:bg-orange-50" : "border-green-100 text-green-600 hover:bg-green-50"}`}>
                {a.published ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
              <button onClick={() => openEdit(a)} className="p-2 border border-[#E5E7EB] hover:bg-[#F3F4F6] transition-colors" data-testid={`edit-article-${a.id}`}><Edit size={13} /></button>
              <button onClick={() => del(a.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50 transition-colors" data-testid={`del-article-${a.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Case Studies Tab ───
function CaseStudiesTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", content: "", cover_image: "", industry: "", metrics: "", published: false });
  const fileRef = useRef();
  const imgRef = useRef();
  const importPdfRef = useRef();
  const [imgUploading, setImgUploading] = useState(false);
  const [importingPdf, setImportingPdf] = useState(false);

  const load = () => axios.get(`${API}/case-studies`, { headers: authH() }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadCoverImage = async () => {
    const file = imgRef.current?.files?.[0];
    if (!file) return;
    setImgUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/upload`, fd, { headers: authH() });
      setForm((prev) => ({ ...prev, cover_image: r.data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Image upload failed"); }
    setImgUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    const metrics = form.metrics.split("\n").map((line) => {
      const [label, value] = line.split(":").map((s) => s.trim());
      return { label, value };
    }).filter((m) => m.label && m.value);
    const payload = { ...form, metrics };
    try {
      if (editing) { await axios.put(`${API}/case-studies/${editing.id}`, payload, { headers: authH() }); toast.success("Case study updated"); }
      else { await axios.post(`${API}/case-studies`, payload, { headers: authH() }); toast.success("Case study created"); }
      setShowForm(false); load();
    } catch { toast.error("Error saving case study"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(`${API}/case-studies/${id}`, { headers: authH() });
    toast.success("Case study deleted");
    load();
  };

  const uploadPdf = async (csId) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/case-studies/${csId}/pdf`, fd, { headers: authH() });
      toast.success("PDF uploaded");
      load();
    } catch { toast.error("PDF upload failed"); }
  };

  const importPdfAsCaseStudy = async () => {
    const file = importPdfRef.current?.files?.[0];
    if (!file) return;
    setImportingPdf(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/case-studies/import-document`, fd, { headers: authH() });
      await load();
      setEditing(res.data);
      setForm({
        ...res.data,
        presentation_mode: res.data.presentation_mode || "pdf",
        content: res.data.content || "",
        metrics: (res.data.metrics || []).map((m) => `${m.label}: ${m.value}`).join("\n")
      });
      setShowForm(true);
      toast.success(res.data.import_notice || "Case study imported and published");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Case study import failed");
    } finally {
      if (importPdfRef.current) importPdfRef.current.value = "";
      setImportingPdf(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", description: "", content: "", presentation_mode: "writeup", cover_image: "", industry: "", metrics: "", published: false });
    setShowForm(true);
  };
  const openEdit = (cs) => {
    setEditing(cs);
    setForm({
      ...cs,
      presentation_mode: cs.presentation_mode || "writeup",
      content: cs.content || "",
      metrics: (cs.metrics || []).map((m) => `${m.label}: ${m.value}`).join("\n")
    });
    setShowForm(true);
  };

  return (
    <div data-testid="case-studies-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280]">Case Studies ({items.length})</p>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer border border-[#E5E7EB] bg-white text-[#0A0A0A] text-xs px-4 py-2 font-semibold hover:bg-[#F3F4F6] transition-colors flex items-center gap-1">
            <Upload size={12} /> {importingPdf ? "Importing..." : "Import PDF"}
            <input ref={importPdfRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={importPdfAsCaseStudy} />
          </label>
          <button onClick={openNew} className="bg-[#002FA7] text-white text-xs px-4 py-2 font-semibold hover:bg-[#001D66] flex items-center gap-1" data-testid="new-cs-btn"><Plus size={12} /> New</button>
        </div>
      </div>
      <p className="text-xs text-[#6B7280] mb-6">
        Upload a case-study PDF and the website will keep the original PDF layout intact on the public page. Add your own cover image if you want a thumbnail card, but the case-study content itself will stay exactly as uploaded.
      </p>

      {showForm && (
        <div className="border border-[#E5E7EB] bg-white p-6 mb-6">
          <div className="flex justify-between mb-4"><h3 className="font-['Outfit'] font-semibold">{editing ? "Edit" : "New"} Case Study</h3><button onClick={() => setShowForm(false)}><X size={16} /></button></div>
          <form onSubmit={save} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="admin-label">Title *</label><input required className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><label className="admin-label">Industry</label><input className="admin-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
            </div>
            <div><label className="admin-label">Description</label><textarea className="admin-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <label className="admin-label">Display Mode</label>
              <select className="admin-input" value={form.presentation_mode || "writeup"} onChange={(e) => setForm({ ...form, presentation_mode: e.target.value })}>
                <option value="writeup">Web write-up</option>
                <option value="pdf">Original PDF layout</option>
              </select>
              <p className="text-[11px] text-[#6B7280] mt-2">
                Use `Original PDF layout` when you want the website page to preserve the uploaded PDF exactly as-is.
              </p>
            </div>
            {form.presentation_mode !== "pdf" && (
              <div>
                <label className="admin-label">Write-up Content</label>
                <p className="text-[11px] text-[#6B7280] mb-2">
                  Use this only when you want a proper web article layout instead of the original PDF pages.
                </p>
                <textarea className="admin-input font-mono text-xs" rows={14} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} />
              </div>
            )}
            <div>
              <label className="admin-label">Cover Image</label>
              <div className="flex gap-2 items-center">
                <input className="admin-input flex-1" placeholder="Paste URL or upload" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
                <label className="cursor-pointer bg-[#F3F4F6] border border-[#E5E7EB] px-3 py-2 text-xs font-medium hover:bg-[#E5E7EB] flex items-center gap-1 whitespace-nowrap">
                  <Upload size={12} /> {imgUploading ? "..." : "Upload"}
                  <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadCoverImage} />
                </label>
              </div>
              {form.cover_image && (
                <img src={form.cover_image.startsWith("/api") ? `${BACKEND}${form.cover_image}` : form.cover_image} alt="" className="mt-2 h-20 object-cover border border-[#E5E7EB]" onError={(e) => { e.target.style.display = "none"; }} />
              )}
            </div>
            <div><label className="admin-label">Metrics (one per line: "Label: Value")</label><textarea className="admin-input font-mono text-xs" rows={5} placeholder="CAC Reduction: 22%&#10;Category Growth: 2x" value={form.metrics} onChange={(e) => setForm({ ...form, metrics: e.target.value })} /></div>
            <div className="flex items-center gap-2"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#002FA7]" /><label className="text-sm">Published (visible on website)</label></div>
            <div className="flex gap-2"><button type="submit" className="bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#001D66]">Save</button><button type="button" onClick={() => setShowForm(false)} className="border border-[#E5E7EB] px-5 py-2 text-sm hover:bg-[#F3F4F6]">Cancel</button></div>
          </form>
        </div>
      )}

      <div className="space-y-2">
        {items.map((cs) => (
          <div key={cs.id} className="border border-[#E5E7EB] bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-medium text-sm text-[#0A0A0A]">{cs.title}</p>
                  <span className={`text-[10px] px-2 py-0.5 font-bold ${cs.published ? "bg-green-50 text-green-700" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                    {cs.published ? "Published" : "Draft"}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 font-bold bg-blue-50 text-[#002FA7]">
                    {(cs.presentation_mode || "writeup") === "pdf" ? "Original PDF" : "Web write-up"}
                  </span>
                </div>
                <p className="text-xs text-[#6B7280]">{cs.industry}</p>
                {cs.pdf_url && <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><Check size={10} /> PDF uploaded</p>}
                {cs.published && (
                  <a href={`/case-studies/${cs.id}`} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline flex items-center gap-1 mt-1">
                    <ExternalLink size={10} /> View public page
                  </a>
                )}
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer p-2 border border-[#E5E7EB] hover:bg-[#F3F4F6] transition-colors" title="Replace source PDF">
                  <Upload size={13} />
                  <input type="file" accept=".pdf" className="hidden" ref={fileRef} onChange={() => uploadPdf(cs.id)} />
                </label>
                <button onClick={() => openEdit(cs)} className="p-2 border border-[#E5E7EB] hover:bg-[#F3F4F6]"><Edit size={13} /></button>
                <button onClick={() => del(cs.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LinkedIn Tab ───
function LinkedInTab() {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", url: "", image_url: "", published_date: "" });

  const load = () => axios.get(`${API}/linkedin-posts`).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await axios.put(`${API}/linkedin-posts/${editing.id}`, form, { headers: authH() }); }
      else { await axios.post(`${API}/linkedin-posts`, form, { headers: authH() }); }
      setShowForm(false); load();
    } catch { alert("Error saving"); }
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; await axios.delete(`${API}/linkedin-posts/${id}`, { headers: authH() }); load(); };

  return (
    <div data-testid="linkedin-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280]">LinkedIn Posts ({posts.length})</p>
        <button onClick={() => { setEditing(null); setForm({ title: "", excerpt: "", url: "", image_url: "", published_date: "" }); setShowForm(true); }} className="bg-[#002FA7] text-white text-xs px-4 py-2 font-semibold hover:bg-[#001D66] flex items-center gap-1" data-testid="new-linkedin-btn"><Plus size={12} /> New Post</button>
      </div>
      {showForm && (
        <div className="border border-[#E5E7EB] bg-white p-6 mb-6">
          <div className="flex justify-between mb-4"><h3 className="font-['Outfit'] font-semibold">{editing ? "Edit" : "New"} Post</h3><button onClick={() => setShowForm(false)}><X size={16} /></button></div>
          <form onSubmit={save} className="space-y-3">
            <div><label className="admin-label">Title *</label><input required className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><label className="admin-label">Excerpt</label><textarea className="admin-input" rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
            <div><label className="admin-label">LinkedIn URL *</label><input required className="admin-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="admin-label">Image URL</label><input className="admin-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
              <div><label className="admin-label">Published Date</label><input type="date" className="admin-input" value={form.published_date?.substring(0, 10)} onChange={(e) => setForm({ ...form, published_date: e.target.value })} /></div>
            </div>
            <div className="flex gap-2"><button type="submit" className="bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#001D66]">Save</button><button type="button" onClick={() => setShowForm(false)} className="border border-[#E5E7EB] px-5 py-2 text-sm">Cancel</button></div>
          </form>
        </div>
      )}
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="border border-[#E5E7EB] bg-white p-4 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0A0A0A] truncate">{p.title}</p>
              <p className="text-xs text-[#9CA3AF] truncate">{p.url}</p>
            </div>
            <div className="flex gap-2">
              <a href={p.url} target="_blank" rel="noreferrer" className="p-2 border border-[#E5E7EB] hover:bg-[#F3F4F6]"><ExternalLink size={13} /></a>
              <button onClick={() => { setEditing(p); setForm(p); setShowForm(true); }} className="p-2 border border-[#E5E7EB] hover:bg-[#F3F4F6]"><Edit size={13} /></button>
              <button onClick={() => del(p.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Subscribers Tab ───
function SubscribersTab() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { axios.get(`${API}/subscribers`, { headers: authH() }).then((r) => setSubs(r.data)).catch(() => {}); }, []);
  const del = async (id) => { await axios.delete(`${API}/subscribers/${id}`, { headers: authH() }); setSubs(subs.filter((s) => s.id !== id)); };
  return (
    <div data-testid="subscribers-tab">
      <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-6">Subscribers ({subs.length})</p>
      <div className="space-y-1">
        {subs.map((s) => (
          <div key={s.id} className="border border-[#E5E7EB] bg-white px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-sm text-[#0A0A0A]">{s.email}</p>
              <div className="flex items-center gap-3 mt-0.5">
                {s.name && <span className="text-xs text-[#6B7280]">{s.name}</span>}
                <span className="text-[10px] bg-blue-50 text-[#002FA7] px-1.5 py-0.5 font-bold">{s.source}</span>
                <span className="text-xs text-[#9CA3AF]">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString("en-IN") : ""}</span>
              </div>
            </div>
            <button onClick={() => del(s.id)} className="p-1.5 text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
          </div>
        ))}
        {subs.length === 0 && <p className="text-[#9CA3AF] text-sm py-8 text-center">No subscribers yet.</p>}
      </div>
    </div>
  );
}

// ─── Comments Tab ───
function CommentsTab() {
  const [comments, setComments] = useState([]);
  const [replyDrafts, setReplyDrafts] = useState({});

  const load = () => {
    axios.get(`${API}/comments`, { headers: authH() }).then((r) => {
      setComments(r.data);
      setReplyDrafts(
        Object.fromEntries(r.data.map((item) => [item.id, item.reply_text || ""]))
      );
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await axios.put(`${API}/comments/${id}/approve`, {}, { headers: authH() });
    load();
  };
  const saveReply = async (id) => {
    const reply_text = (replyDrafts[id] || "").trim();
    await axios.put(`${API}/comments/${id}/reply`, { reply_text }, { headers: authH() });
    load();
  };
  const del = async (id) => {
    await axios.delete(`${API}/comments/${id}`, { headers: authH() });
    load();
  };
  return (
    <div data-testid="comments-tab">
      <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-6">Comments ({comments.length})</p>
      <div className="space-y-2">
        {comments.map((c) => (
          <div key={c.id} className={`border bg-white p-4 ${!c.approved ? "border-orange-200" : "border-[#E5E7EB]"}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-[#0A0A0A]">{c.author_name}</p>
                  <span className="text-xs text-[#9CA3AF]">{c.target_label || "Homepage"}</span>
                  {!c.approved && <span className="text-[10px] bg-orange-50 text-orange-600 px-2 py-0.5 font-bold">Pending</span>}
                </div>
                <p className="text-xs text-[#9CA3AF] mb-2">{c.author_email}</p>
                <p className="text-sm text-[#4B5563]">{c.content}</p>
                {c.reply_text && (
                  <div className="mt-3 border-l-2 border-[#002FA7] bg-blue-50/60 px-3 py-2">
                    <p className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#002FA7] mb-1">Reply from Niharika</p>
                    <p className="text-sm text-[#33507A]">{c.reply_text}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {!c.approved && <button onClick={() => approve(c.id)} className="p-2 border border-green-200 text-green-600 hover:bg-green-50"><Check size={13} /></button>}
                <button onClick={() => del(c.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="border-t border-[#F3F4F6] pt-3">
              <label className="admin-label">Public reply</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <textarea
                  rows={2}
                  className="admin-input flex-1"
                  placeholder="Write one public reply that appears below this comment."
                  value={replyDrafts[c.id] || ""}
                  onChange={(e) => setReplyDrafts({ ...replyDrafts, [c.id]: e.target.value })}
                />
                <button
                  onClick={() => saveReply(c.id)}
                  className="bg-[#002FA7] text-white px-4 py-2 text-xs font-semibold hover:bg-[#001D66] self-start"
                >
                  Save Reply
                </button>
              </div>
            </div>
          </div>
        ))}
        {comments.length === 0 && <p className="text-[#9CA3AF] text-sm py-8 text-center">No comments yet.</p>}
      </div>
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [replyDrafts, setReplyDrafts] = useState({});

  const load = () => {
    axios.get(`${API}/admin/reviews`, { headers: authH() }).then((r) => {
      setReviews(r.data);
      setReplyDrafts(
        Object.fromEntries(r.data.map((item) => [item.id, item.reply_text || ""]))
      );
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await axios.put(`${API}/reviews/${id}/approve`, {}, { headers: authH() });
    load();
  };

  const saveReply = async (id) => {
    const reply_text = (replyDrafts[id] || "").trim();
    await axios.put(`${API}/reviews/${id}/reply`, { reply_text }, { headers: authH() });
    load();
  };

  const del = async (id) => {
    await axios.delete(`${API}/reviews/${id}`, { headers: authH() });
    load();
  };

  return (
    <div data-testid="reviews-tab">
      <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-6">Reviews ({reviews.length})</p>
      <div className="space-y-3">
        {reviews.map((review) => (
          <div key={review.id} className={`border bg-white p-4 ${!review.approved ? "border-orange-200" : "border-[#E5E7EB]"}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-medium text-[#0A0A0A]">{review.author_name}</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={12}
                        className={star <= review.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D1D5DB]"}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-[#9CA3AF]">{review.target_label || "Homepage"}</span>
                  {!review.approved && <span className="text-[10px] bg-orange-50 text-orange-600 px-2 py-0.5 font-bold">Pending</span>}
                </div>
                <p className="text-xs text-[#9CA3AF] mb-2">{review.author_email}</p>
                <p className="text-sm text-[#4B5563]">{review.content}</p>
                {review.reply_text && (
                  <div className="mt-3 border-l-2 border-[#002FA7] bg-blue-50/60 px-3 py-2">
                    <p className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#002FA7] mb-1">Reply from Niharika</p>
                    <p className="text-sm text-[#33507A]">{review.reply_text}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {!review.approved && <button onClick={() => approve(review.id)} className="p-2 border border-green-200 text-green-600 hover:bg-green-50"><Check size={13} /></button>}
                <button onClick={() => del(review.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="border-t border-[#F3F4F6] pt-3">
              <label className="admin-label">Public reply</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <textarea
                  rows={2}
                  className="admin-input flex-1"
                  placeholder="Write one public reply that appears below this review."
                  value={replyDrafts[review.id] || ""}
                  onChange={(e) => setReplyDrafts({ ...replyDrafts, [review.id]: e.target.value })}
                />
                <button
                  onClick={() => saveReply(review.id)}
                  className="bg-[#002FA7] text-white px-4 py-2 text-xs font-semibold hover:bg-[#001D66] self-start"
                >
                  Save Reply
                </button>
              </div>
            </div>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-[#9CA3AF] text-sm py-8 text-center">No reviews yet.</p>}
      </div>
    </div>
  );
}

// ─── Settings Tab ───
function SettingsTab() {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aboutForm, setAboutForm] = useState({ tagline: "", bio_short: "", social_linkedin: "", social_twitter: "", social_email: "" });
  const [aboutSaving, setAboutSaving] = useState(false);
  const photoRef = useRef();

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: authH() }).then((r) => setPhotoUrl(r.data?.profile_photo)).catch(() => {});
    axios.get(`${API}/admin/about-content`, { headers: authH() }).then((r) => setAboutForm(r.data)).catch(() => {});
  }, []);

  const uploadPhoto = async () => {
    const file = photoRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/photo`, fd, { headers: authH() });
      setPhotoUrl(r.data.url);
    } catch { alert("Upload failed"); }
    setUploading(false);
  };

  const deletePhoto = async () => {
    await axios.delete(`${API}/admin/photo`, { headers: authH() });
    setPhotoUrl(null);
  };

  const saveAbout = async (e) => {
    e.preventDefault();
    setAboutSaving(true);
    try {
      await axios.put(`${API}/admin/about-content`, aboutForm, { headers: authH() });
      alert("Saved!");
    } catch { alert("Save failed"); }
    setAboutSaving(false);
  };

  return (
    <div data-testid="settings-tab" className="space-y-8">
      <div>
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-6">Settings</p>

        {/* Profile Photo (shows on About page) */}
        <div className="border border-[#E5E7EB] bg-white p-6 mb-6">
          <p className="font-['Outfit'] font-semibold text-[#0A0A0A] mb-1">Profile Photo</p>
          <p className="text-xs text-[#6B7280] mb-4">This photo is displayed on the <strong>About page</strong>. Upload your professional headshot here.</p>
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 border border-[#E5E7EB] overflow-hidden flex-shrink-0">
              {photoUrl ? (
                <img src={`${BACKEND}${photoUrl}`} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF] font-['Outfit'] font-bold text-xl">NP</div>
              )}
            </div>
            <div className="flex gap-3 flex-wrap">
              <label className="cursor-pointer bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#001D66] transition-colors flex items-center gap-2" data-testid="upload-photo-btn">
                <Upload size={13} /> {uploading ? "Uploading..." : "Upload Photo"}
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
              </label>
              {photoUrl && (
                <button onClick={deletePhoto} className="border border-red-200 text-red-500 px-4 py-2 text-sm hover:bg-red-50 flex items-center gap-2" data-testid="delete-photo-btn">
                  <Trash2 size={13} /> Remove
                </button>
              )}
            </div>
          </div>
        </div>

        {/* About Page Content */}
        <div className="border border-[#E5E7EB] bg-white p-6 mb-6">
          <p className="font-['Outfit'] font-semibold text-[#0A0A0A] mb-1">About Page Content</p>
          <p className="text-xs text-[#6B7280] mb-4">Edit the tagline quote and social links shown on your About page and footer.</p>
          <form onSubmit={saveAbout} className="space-y-4">
            <div>
              <label className="admin-label">Tagline / Quote</label>
              <textarea rows={2} className="admin-input" placeholder="Building brands that consumers trust..." value={aboutForm.tagline} onChange={(e) => setAboutForm({ ...aboutForm, tagline: e.target.value })} />
            </div>
            <div>
              <label className="admin-label">Short Bio (optional override)</label>
              <textarea rows={2} className="admin-input" placeholder="Leave blank to use the default bio text..." value={aboutForm.bio_short} onChange={(e) => setAboutForm({ ...aboutForm, bio_short: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="admin-label">LinkedIn URL</label>
                <input className="admin-input" placeholder="https://linkedin.com/in/..." value={aboutForm.social_linkedin} onChange={(e) => setAboutForm({ ...aboutForm, social_linkedin: e.target.value })} />
              </div>
              <div>
                <label className="admin-label">Twitter / X URL</label>
                <input className="admin-input" placeholder="https://twitter.com/..." value={aboutForm.social_twitter} onChange={(e) => setAboutForm({ ...aboutForm, social_twitter: e.target.value })} />
              </div>
              <div>
                <label className="admin-label">Contact Email</label>
                <input className="admin-input" placeholder="you@email.com" value={aboutForm.social_email} onChange={(e) => setAboutForm({ ...aboutForm, social_email: e.target.value })} />
              </div>
            </div>
            <button type="submit" disabled={aboutSaving} className="bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#001D66] flex items-center gap-2 disabled:opacity-50" data-testid="save-about-btn">
              <Save size={13} /> {aboutSaving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Leads Tab ───
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { axios.get(`${API}/leads`, { headers: authH() }).then((r) => setLeads(r.data)).catch(() => {}); }, []);
  const del = async (id) => { await axios.delete(`${API}/leads/${id}`, { headers: authH() }); setLeads(leads.filter((l) => l.id !== id)); };
  return (
    <div data-testid="leads-tab">
      <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-6">Leads / Enquiries ({leads.length})</p>
      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.id} className="border border-[#E5E7EB] bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <p className="font-semibold text-sm text-[#0A0A0A]">{l.name}</p>
                  {l.company && <span className="text-xs text-[#6B7280] border border-[#E5E7EB] px-2 py-0.5">{l.company}</span>}
                  {l.stage && <span className="text-[10px] bg-blue-50 text-[#002FA7] px-2 py-0.5 font-bold">{l.stage}</span>}
                </div>
                <a href={`mailto:${l.email}`} className="text-xs text-[#002FA7] hover:underline">{l.email}</a>
                {l.topic && <p className="text-xs text-[#9CA3AF] mt-1">Topic: {l.topic}</p>}
                {l.challenge && <p className="text-sm text-[#4B5563] mt-2 leading-relaxed border-l-2 border-[#002FA7]/20 pl-3">{l.challenge}</p>}
                <p className="text-[10px] text-[#9CA3AF] mt-2">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : ""}</p>
              </div>
              <div className="flex gap-2">
                <a href={`mailto:${l.email}`} className="p-2 border border-[#002FA7]/20 text-[#002FA7] hover:bg-blue-50 transition-colors text-xs font-medium flex items-center gap-1">Reply</a>
                <button onClick={() => del(l.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {leads.length === 0 && <p className="text-[#9CA3AF] text-sm py-8 text-center">No leads yet. Share your "Work With Me" page!</p>}
      </div>
    </div>
  );
}

// ─── AI Agents Tab ───
function AIAgentsTab() {
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", status: "Coming Soon", video_url: "", order: 0 });

  const load = () => axios.get(`${API}/ai-agents`).then((r) => setAgents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: parseInt(form.order) || 0 };
    if (editing) { await axios.put(`${API}/ai-agents/${editing.id}`, payload, { headers: authH() }); }
    else { await axios.post(`${API}/ai-agents`, payload, { headers: authH() }); }
    setShowForm(false); setEditing(null);
    setForm({ title: "", description: "", status: "Coming Soon", video_url: "", order: 0 });
    load();
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; await axios.delete(`${API}/ai-agents/${id}`, { headers: authH() }); load(); };

  return (
    <div data-testid="ai-agents-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280]">AI Agent Cards ({agents.length})</p>
        <button onClick={() => { setEditing(null); setForm({ title: "", description: "", status: "Coming Soon", video_url: "", order: agents.length }); setShowForm(true); }} className="bg-[#002FA7] text-white text-xs px-4 py-2 font-semibold hover:bg-[#001D66] flex items-center gap-1" data-testid="new-agent-btn"><Plus size={12} /> New Agent</button>
      </div>

      {showForm && (
        <form onSubmit={save} className="border border-[#002FA7]/20 bg-blue-50/30 p-5 mb-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="admin-label">Title *</label><input required className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div>
              <label className="admin-label">Status</label>
              <select className="admin-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option>Coming Soon</option>
                <option>Building</option>
                <option>Live</option>
              </select>
            </div>
          </div>
          <div><label className="admin-label">Description *</label><textarea required rows={3} className="admin-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="admin-label">Video URL (YouTube / external)</label><input className="admin-input" placeholder="https://youtube.com/..." value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} /></div>
            <div><label className="admin-label">Display Order</label><input type="number" className="admin-input" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-[#002FA7] text-white px-4 py-2 text-xs font-semibold hover:bg-[#001D66]">{editing ? "Update" : "Create"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="border border-[#E5E7EB] px-4 py-2 text-xs text-[#6B7280] hover:bg-[#F3F4F6]">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {agents.map((a) => (
          <div key={a.id} className="border border-[#E5E7EB] bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-sm text-[#0A0A0A]">{a.title}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 ${a.status === "Building" ? "bg-[#002FA7] text-white" : a.status === "Live" ? "bg-green-100 text-green-700" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>{a.status}</span>
                </div>
                <p className="text-xs text-[#6B7280] leading-relaxed">{a.description}</p>
                {a.video_url && <p className="text-[10px] text-[#002FA7] mt-1 flex items-center gap-1"><Video size={10} /> Video attached</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => { setEditing(a); setForm({ title: a.title, description: a.description, status: a.status, video_url: a.video_url || "", order: a.order || 0 }); setShowForm(true); }} className="p-2 border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]"><Edit size={13} /></button>
                <button onClick={() => del(a.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {agents.length === 0 && <p className="text-[#9CA3AF] text-sm py-8 text-center">No AI agent cards yet.</p>}
      </div>
    </div>
  );
}

// ─── SaaS Comparisons Tab ───
function SaaSComparisonsTab() {
  const [comps, setComps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const BLANK = { title: "", description: "", category: "", status: "Coming Soon", order: 0 };
  const [form, setForm] = useState(BLANK);

  const load = () => axios.get(`${API}/saas-comparisons`).then((r) => setComps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: parseInt(form.order) || 0 };
    if (editing) { await axios.put(`${API}/saas-comparisons/${editing.id}`, payload, { headers: authH() }); }
    else { await axios.post(`${API}/saas-comparisons`, payload, { headers: authH() }); }
    setShowForm(false); setEditing(null); setForm(BLANK); load();
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; await axios.delete(`${API}/saas-comparisons/${id}`, { headers: authH() }); load(); };

  return (
    <div data-testid="saas-comparisons-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#6B7280]">SaaS Comparisons ({comps.length})</p>
        <button onClick={() => { setEditing(null); setForm({ ...BLANK, order: comps.length }); setShowForm(true); }} className="bg-[#002FA7] text-white text-xs px-4 py-2 font-semibold hover:bg-[#001D66] flex items-center gap-1" data-testid="new-comparison-btn"><Plus size={12} /> New Comparison</button>
      </div>

      {showForm && (
        <form onSubmit={save} className="border border-[#002FA7]/20 bg-blue-50/30 p-5 mb-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="admin-label">Title *</label><input required className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><label className="admin-label">Category</label><input className="admin-input" placeholder="e.g. Automation, CRM" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          </div>
          <div><label className="admin-label">Description *</label><textarea required rows={2} className="admin-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="admin-label">Status</label>
              <select className="admin-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option>Coming Soon</option>
                <option>Published</option>
              </select>
            </div>
            <div><label className="admin-label">Display Order</label><input type="number" className="admin-input" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-[#002FA7] text-white px-4 py-2 text-xs font-semibold hover:bg-[#001D66]">{editing ? "Update" : "Create"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="border border-[#E5E7EB] px-4 py-2 text-xs text-[#6B7280] hover:bg-[#F3F4F6]">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {comps.map((c) => (
          <div key={c.id} className="border border-[#E5E7EB] bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="font-semibold text-sm text-[#0A0A0A]">{c.title}</p>
                  {c.category && <span className="text-[10px] border border-[#E5E7EB] px-2 py-0.5 text-[#6B7280]">{c.category}</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 ${c.status === "Published" ? "bg-green-100 text-green-700" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>{c.status}</span>
                </div>
                <p className="text-xs text-[#6B7280] leading-relaxed">{c.description}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => { setEditing(c); setForm({ title: c.title, description: c.description, category: c.category || "", status: c.status, order: c.order || 0 }); setShowForm(true); }} className="p-2 border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]"><Edit size={13} /></button>
                <button onClick={() => del(c.id)} className="p-2 border border-red-100 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {comps.length === 0 && <p className="text-[#9CA3AF] text-sm py-8 text-center">No comparisons yet.</p>}
      </div>
    </div>
  );
}

// ─── Main Admin Dashboard ───
const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "content", label: "Site Content", icon: FileText },
  { id: "articles", label: "Articles", icon: BookOpen },
  { id: "linkedin", label: "LinkedIn Reads", icon: Linkedin },
  { id: "case-studies", label: "Case Studies", icon: Briefcase },
  { id: "leads", label: "Leads", icon: UserPlus },
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "comments", label: "Comments", icon: MessageSquare },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(!!getToken());
  const [adminEmail, setAdminEmail] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset")) {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
      setAdminEmail("");
    }
    const token = getToken();
    if (token && !params.get("reset")) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { setAuthed(true); setAdminEmail(r.data.email); })
        .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
    }
  }, []);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  if (!authed) return <LoginPage onLogin={(email) => { setAuthed(true); setAdminEmail(email); }} />;

  const ActiveComponent = {
    overview: OverviewTab, content: ContentTab, articles: ArticlesTab, linkedin: LinkedInTab, "case-studies": CaseStudiesTab,
    leads: LeadsTab, subscribers: SubscribersTab, comments: CommentsTab, reviews: ReviewsTab, settings: SettingsTab,
  }[activeTab] || OverviewTab;

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-56 bg-white border-r border-[#E5E7EB] flex flex-col transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-[#E5E7EB]">
          <p className="font-['Outfit'] font-semibold text-[#0A0A0A] text-sm">Niharika Patel</p>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">Admin Studio</p>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              data-testid={`tab-${tab.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors text-left ${
                activeTab === tab.id ? "bg-[#002FA7] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6]"
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E5E7EB]">
          <button onClick={logout} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#EF4444] hover:bg-red-50 transition-colors" data-testid="logout-btn">
            <LogOut size={14} /> Log Out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="bg-white border-b border-[#E5E7EB] px-6 h-14 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-1.5 hover:bg-[#F3F4F6] transition-colors" onClick={() => setSidebarOpen(true)}>
              <LayoutDashboard size={18} />
            </button>
            <p className="font-['Outfit'] font-semibold text-[#0A0A0A] capitalize">{activeTab.replace("-", " ")}</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={BACKEND}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] border border-[#002FA7]/30 px-3 py-1.5 hover:bg-[#002FA7] hover:text-white transition-colors"
              data-testid="view-site-btn"
            >
              <ExternalLink size={12} /> View Site
            </a>
            <p className="text-xs text-[#9CA3AF] hidden sm:block">{adminEmail}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-w-5xl">
          {/* Add global admin styles */}
          <style>{`
            .admin-label { display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #0A0A0A; margin-bottom: 4px; }
            .admin-input { width: 100%; border: 1px solid #E5E7EB; padding: 8px 12px; font-size: 13px; outline: none; }
            .admin-input:focus { border-color: #002FA7; }
          `}</style>
          <ActiveComponent />
        </div>
      </main>
    </div>
  );
}
